import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { usePortalImport } from '@/hooks/use-portal'
import { downloadPortalImportTemplate } from '@/services/portal.service'
import type { PortalImportResponse } from '@/services/portal.service'
import { CheckCircle, Download, FileSpreadsheet, Loader2, Upload, XCircle } from 'lucide-react'
import type React from 'react'
import { useCallback, useState } from 'react'

const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx', '.xls']

function isAcceptedFile(filename: string): boolean {
  const lower = filename.toLowerCase()
  return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext))
}

export function PortalImportPage() {
  const [file, setFile] = useState<File | null>(null)
  const [dragActive, setDragActive] = useState(false)
  const [result, setResult] = useState<PortalImportResponse | null>(null)

  const importMutation = usePortalImport()

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files?.[0]) {
      const droppedFile = e.dataTransfer.files[0]
      if (isAcceptedFile(droppedFile.name)) {
        setFile(droppedFile)
        setResult(null)
      }
    }
  }, [])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setFile(e.target.files[0])
      setResult(null)
    }
  }

  const handleImport = async () => {
    if (!file) return
    try {
      const response = await importMutation.mutateAsync(file)
      setResult(response)
    } catch (error) {
      console.error('Import failed:', error)
    }
  }

  return (
    <div className="p-8 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Import People</h1>
        <p className="text-muted-foreground">Upload a CSV or Excel file to import people</p>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <div className="flex items-start gap-3">
          <svg
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-blue-600 mt-0.5 shrink-0"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
          </svg>
          <div>
            <p className="text-sm font-medium text-blue-900">Your data is secure</p>
            <p className="text-xs text-blue-700 mt-1">
              All uploads are encrypted in transit and at rest. Data is stored securely in AWS with
              SOC 2 compliant infrastructure. Only authorized HR administrators in your organization
              can access this information.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Upload Section */}
        <Card>
          <CardHeader>
            <CardTitle>Upload File</CardTitle>
            <CardDescription>Upload a CSV or Excel (.xlsx) file with people data</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
                <p className="text-sm font-medium">
                  {file ? file.name : 'Drop your file here or click to browse'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">CSV or Excel files, max 10MB</p>
              </label>
            </div>

            {file && (
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFile(null)
                    setResult(null)
                  }}
                >
                  Remove
                </Button>
              </div>
            )}

            <Button
              onClick={handleImport}
              disabled={!file || importMutation.isPending}
              className="w-full"
            >
              {importMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Import People
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Format Guide */}
        <Card>
          <CardHeader>
            <CardTitle>File Format</CardTitle>
            <CardDescription>Your file should include these columns</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button variant="outline" onClick={downloadPortalImportTemplate} className="w-full">
              <Download className="h-4 w-4 mr-2" />
              Download CSV Template
            </Button>

            <div className="rounded-lg bg-muted p-4 font-mono text-xs overflow-x-auto">
              <p className="text-muted-foreground">
                first_name,last_name,email,location,phone_number,birth_date,seniority
              </p>
              <p>John,Doe,john@example.com,New York,+15551234567,1990-01-15,Senior</p>
              <p>Jane,Smith,jane@example.com,London,+447911123456,1985-06-20,</p>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Column Requirements:</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>
                  <strong>first_name</strong>, <strong>last_name</strong>, <strong>email</strong> -
                  Required
                </li>
                <li>
                  <strong>location</strong> - Required
                </li>
                <li>
                  <strong>phone_number</strong> - Recommended (E.164 format)
                </li>
                <li>
                  <strong>birth_date</strong> - Optional (YYYY-MM-DD)
                </li>
                <li>
                  <strong>seniority</strong> - Optional (free text)
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Results */}
      {result && (
        <Card>
          <CardHeader>
            <CardTitle>Import Results</CardTitle>
            <CardDescription>
              {result.success_count} of {result.total} people imported successfully
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4">
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">{result.success_count} Successful</span>
              </div>
              {result.failure_count > 0 && (
                <div className="flex items-center gap-2 text-red-600">
                  <XCircle className="h-5 w-5" />
                  <span className="font-medium">{result.failure_count} Failed</span>
                </div>
              )}
            </div>

            {result.warnings.length > 0 && (
              <div className="rounded-md bg-yellow-50 p-3">
                <p className="text-sm font-medium text-yellow-800 mb-1">Warnings</p>
                {result.warnings.map((w, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: warnings list is static after render
                  <p key={i} className="text-xs text-yellow-700">
                    {w}
                  </p>
                ))}
              </div>
            )}

            {result.failure_count > 0 && (
              <div className="rounded-lg border">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="h-10 px-4 text-left text-sm font-medium">Row</th>
                      <th className="h-10 px-4 text-left text-sm font-medium">Status</th>
                      <th className="h-10 px-4 text-left text-sm font-medium">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.results
                      .filter((r) => !r.success)
                      .map((r) => (
                        <tr key={r.row} className="border-b">
                          <td className="p-4 text-sm">{r.row}</td>
                          <td className="p-4">
                            <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-red-100 text-red-700">
                              Failed
                            </span>
                          </td>
                          <td className="p-4 text-sm text-muted-foreground">{r.error}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
